import { useEffect, type ReactNode } from 'react'
import ContentViewerDesktop from '@/content-viewer/components/ContentViewerColumn'
import ContentViewerMobile from '@/content-viewer/components/ContentViewerSlideover'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'
import { selectIsContentViewerOpen } from '@/content-viewer/store/selectors'

type DynamicContentViewerProps = {
  children: ReactNode
}

/**
 * DynamicContentViewer
 *
 * Owns the split-view layout logic:
 * - Desktop: left pane becomes 50% width and viewer is rendered in a right pane when `showContentViewer` is true.
 * - Small/mobile: left pane stays full width; viewer is rendered as a slideover.
 */
export function DynamicContentViewer(props: DynamicContentViewerProps) {
  const { children } = props

  const dispatch = useAppDispatch()

  const showContentViewer = useAppSelector((state: RootState) => selectIsContentViewerOpen(state))

  // Clear viewer state when leaving the user-files drive view entirely.
  // Breakpoint changes should not unmount this component, so the viewer won't close.
  useEffect(() => {
    return () => {
      dispatch(ContentViewerActions.clearContentViewer())
    }
  }, [dispatch])

  const deviceType = useDeviceType()
  const isSmall = deviceType !== DEVICE_TYPE.DESKTOP

  if (isSmall) {
    return (
      <>
        <div className="w-full min-w-0 flex flex-col">{children}</div>
        <ContentViewerMobile open={showContentViewer} conversationId="none" />
      </>
    )
  }

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <div className={`min-w-0 flex flex-col transition-all duration-200 ${showContentViewer ? 'w-1/2' : 'w-full'}`}>{children}</div>

      <div
        className={`min-w-0 h-full flex flex-col overflow-hidden overflow-x-hidden transition-all duration-200 ${
          showContentViewer ? 'w-1/2 opacity-100 translate-x-0' : 'w-0 opacity-0 translate-x-4 pointer-events-none'
        }`}
      >
        <ContentViewerDesktop conversationId="none" />
      </div>
    </div>
  )
}
